import { __extends } from "tslib";
import { AssetEvent } from '@awayjs/core';
import { ShaderBase } from '@awayjs/renderer';
import { LightSources } from '../lightpickers/LightSources';
import { MethodEvent } from '../events/MethodEvent';
import { EffectColorTransformMethod } from '../methods/EffectColorTransformMethod';
import { PassBase } from '../passes/PassBase';
import { LightingShader } from '../shaders/LightingShader';
import { MethodPassMode } from './MethodPassMode';
/**
 * CompiledPass forms an abstract base class for the default compiled pass materials provided by Away3D,
 * using material methods to define their appearance.
 */
var MethodPass = /** @class */ (function (_super) {
    __extends(MethodPass, _super);
    /**
     * Creates a new CompiledPass object.
     *
     * @param material The material to which this pass belongs.
     */
    function MethodPass(mode, renderMaterial, renderElements) {
        var _this = _super.call(this, renderMaterial, renderElements) || this;
        _this._maxLights = 3;
        _this._mode = 0x03;
        _this._includeCasters = true;
        _this._chunks = new Array();
        _this._methods = new Array();
        _this._numEffectDependencies = 0;
        _this.numDirectionalLights = 0;
        _this.numPointLights = 0;
        _this.numLightProbes = 0;
        _this.pointLightsOffset = 0;
        _this.directionalLightsOffset = 0;
        _this.lightProbesOffset = 0;
        _this._mode = mode;
        _this._onLightsChangeDelegate = function (event) { return _this.onLightsChange(event); };
        _this._onMethodInvalidatedDelegate = function (event) { return _this.onMethodInvalidated(event); };
        _this.lightPicker = renderMaterial.lightPicker;
        if (_this._shader == null)
            _this._updateShader();
        return _this;
    }
    Object.defineProperty(MethodPass.prototype, "mode", {
        /**
         *
         */
        get: function () {
            return this._mode;
        },
        set: function (value) {
            if (this._mode == value)
                return;
            this._mode = value;
            this._updateLights();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "includeCasters", {
        /**
         * Indicates whether or not shadow casting lights need to be included.
         */
        get: function () {
            return this._includeCasters;
        },
        set: function (value) {
            if (this._includeCasters == value)
                return;
            this._includeCasters = value;
            this._updateLights();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "lightPicker", {
        /**
         *
         * @returns {LightPickerBase}
         */
        get: function () {
            return this._lightPicker;
        },
        set: function (value) {
            //if (this._lightPicker == value)
            //	return;
            if (this._lightPicker)
                this._lightPicker.removeEventListener(AssetEvent.INVALIDATE, this._onLightsChangeDelegate);
            this._lightPicker = value;
            if (this._lightPicker)
                this._lightPicker.addEventListener(AssetEvent.INVALIDATE, this._onLightsChangeDelegate);
            this._updateLights();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "enableLightFallOff", {
        /**
         * Whether or not to use fallOff and radius properties for lights. This can be used to improve performance and
         * compatibility for constrained mode.
         */
        get: function () {
            return this._renderMaterial.enableLightFallOff;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "diffuseLightSources", {
        /**
         * Define which light source types to use for diffuse reflections. This allows choosing between regular lights
         * and/or light probes for diffuse reflections.
         *
         * @see away3d.materials.LightSources
         */
        get: function () {
            return this._renderMaterial.diffuseLightSources;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "specularLightSources", {
        /**
         * Define which light source types to use for specular reflections. This allows choosing between regular lights
         * and/or light probes for specular reflections.
         *
         * @see away3d.materials.LightSources
         */
        get: function () {
            return this._renderMaterial.specularLightSources;
        },
        enumerable: false,
        configurable: true
    });
    MethodPass.prototype._updateShader = function () {
        if ((this.numDirectionalLights || this.numPointLights || this.numLightProbes) && !(this._shader instanceof LightingShader)) {
            if (this._shader != null) {
                this._shader.dispose();
                this._shader = null;
            }
            this._shader = new LightingShader(this._renderElements, this._renderMaterial, this, this._stage);
        }
        else if (this._shader == null) { // !(_shader instanceof ShaderBase) because there are only two shader types atm
            if (this._shader != null) {
                this._shader.dispose();
                this._shader = null;
            }
            this._shader = new ShaderBase(this._renderElements, this._renderMaterial, this, this._stage);
        }
    };
    /**
     * Initializes the unchanging constant data for this material.
     */
    MethodPass.prototype._initConstantData = function () {
        //Updates method constants if they have changed.
        var len = this._chunks.length;
        for (var i = 0; i < len; ++i)
            this._chunks[i]._initConstants();
    };
    Object.defineProperty(MethodPass.prototype, "colorTransform", {
        /**
         * The ColorTransform object to transform the colour of the material with. Defaults to null.
         */
        get: function () {
            return this.colorTransformMethod ? this.colorTransformMethod.colorTransform : null;
        },
        set: function (value) {
            if (value) {
                if (this.colorTransformMethod == null)
                    this.colorTransformMethod = new EffectColorTransformMethod();
                this.colorTransformMethod.colorTransform = value;
            }
            else if (!value) {
                if (this.colorTransformMethod)
                    this.colorTransformMethod = null;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "colorTransformMethod", {
        /**
         * The EffectColorTransformMethod object to transform the colour of the material with. Defaults to null.
         */
        get: function () {
            return this._colorTransformMethod;
        },
        set: function (value) {
            if (this._colorTransformMethod == value)
                return;
            if (this._colorTransformMethod) {
                this._removeDependency(this._colorTransformMethod);
                this._colorTransformChunk = null;
            }
            this._colorTransformMethod = value;
            if (value) {
                this._colorTransformChunk = value.getAbstraction(this._shader);
                this._addDependency(value);
            }
        },
        enumerable: false,
        configurable: true
    });
    MethodPass.prototype._removeDependency = function (method, effectsDependency) {
        if (effectsDependency === void 0) { effectsDependency = false; }
        var index = this._methods.indexOf(method);
        if (index == -1)
            return;
        if (!effectsDependency)
            this._numEffectDependencies--;
        method.removeEventListener(MethodEvent.SHADER_INVALIDATED, this._onMethodInvalidatedDelegate);
        this._chunks[index].onClear(null);
        this._methods.splice(index, 1);
        this._chunks.splice(index, 1);
        this.invalidate();
    };
    MethodPass.prototype._addDependency = function (method, effectsDependency, index) {
        if (effectsDependency === void 0) { effectsDependency = false; }
        if (index === void 0) { index = -1; }
        method.addEventListener(MethodEvent.SHADER_INVALIDATED, this._onMethodInvalidatedDelegate);
        var chunk = method.getAbstraction(this._shader);
        if (effectsDependency) {
            if (index != -1) {
                this._methods.splice(index + this._methods.length - this._numEffectDependencies, 0, method);
                this._chunks.splice(index + this._chunks.length - this._numEffectDependencies, 0, chunk);
            }
            else {
                this._methods.push(method);
                this._chunks.push(chunk);
            }
            this._numEffectDependencies++;
        }
        else {
            this._methods.splice(this._methods.length - this._numEffectDependencies, 0, method);
            this._chunks.splice(this._chunks.length - this._numEffectDependencies, 0, chunk);
        }
        this.invalidate();
    };
    /**
     * Appends an "effect" shading method to the shader. Effect methods are those that do not influence the lighting
     * but modulate the shaded colour, used for fog, outlines, etc. The method will be applied to the result of the
     * methods added prior.
     */
    MethodPass.prototype.addEffectMethod = function (method) {
        this._addDependency(method, true);
    };
    Object.defineProperty(MethodPass.prototype, "numEffectMethods", {
        /**
         * The number of "effect" methods added to the material.
         */
        get: function () {
            return this._numEffectDependencies;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Queries whether a given effects method was added to the material.
     *
     * @param method The method to be queried.
     * @return true if the method was added to the material, false otherwise.
     */
    MethodPass.prototype.hasEffectMethod = function (method) {
        return this._methods.indexOf(method) != -1;
    };
    /**
     * Returns the method added at the given index.
     * @param index The index of the method to retrieve.
     * @return The method at the given index.
     */
    MethodPass.prototype.getEffectMethodAt = function (index) {
        if (index < 0 || index > this._numEffectDependencies - 1)
            return null;
        return this._methods[index + this._methods.length - this._numEffectDependencies];
    };
    /**
     * Adds an effect method at the specified index amongst the methods already added to the material. Effect
     * methods are those that do not influence the lighting but modulate the shaded colour, used for fog, outlines,
     * etc. The method will be applied to the result of the methods with a lower index.
     */
    MethodPass.prototype.addEffectMethodAt = function (method, index) {
        this._addDependency(method, true, index);
    };
    /**
     * Removes an effect method from the material.
     * @param method The method to be removed.
     */
    MethodPass.prototype.removeEffectMethod = function (method) {
        this._removeDependency(method, true);
    };
    /**
     * remove an effect method at the specified index from the material.
     */
    MethodPass.prototype.removeEffectMethodAt = function (index) {
        var method = this.getEffectMethodAt(index);
        if (method != null)
            this._removeDependency(method, true);
    };
    Object.defineProperty(MethodPass.prototype, "normalMethod", {
        /**
         * The method used to generate the per-pixel normals. Defaults to NormalBasicMethod.
         */
        get: function () {
            return this._normalMethod;
        },
        set: function (value) {
            if (this._normalMethod == value)
                return;
            if (this._normalMethod) {
                this._removeDependency(this._normalMethod);
                this._normalChunk = null;
            }
            this._normalMethod = value;
            if (value) {
                this._normalChunk = value.getAbstraction(this._shader);
                this._addDependency(value);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "ambientMethod", {
        /**
         * The method that provides the ambient lighting contribution. Defaults to AmbientBasicMethod.
         */
        get: function () {
            return this._ambientMethod;
        },
        set: function (value) {
            if (this._ambientMethod == value)
                return;
            if (this._ambientMethod) {
                this._removeDependency(this._ambientMethod);
                this._ambientChunk = null;
            }
            this._ambientMethod = value;
            if (value) {
                this._ambientChunk = value.getAbstraction(this._shader);
                this._addDependency(value);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "shadowMethod", {
        /**
         * The method used to render shadows cast on this surface, or null if no shadows are to be rendered. Defaults to null.
         */
        get: function () {
            return this._shadowMethod;
        },
        set: function (value) {
            if (this._shadowMethod == value)
                return;
            if (this._shadowMethod) {
                this._removeDependency(this._shadowMethod);
                this._shadowChunk = null;
            }
            this._shadowMethod = value;
            if (value) {
                this._shadowChunk = value.getAbstraction(this._shader);
                this._addDependency(value);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "diffuseMethod", {
        /**
         * The method that provides the diffuse lighting contribution. Defaults to DiffuseBasicMethod.
         */
        get: function () {
            return this._diffuseMethod;
        },
        set: function (value) {
            if (this._diffuseMethod == value)
                return;
            if (this._diffuseMethod) {
                this._removeDependency(this._diffuseMethod);
                this._diffuseChunk = null;
            }
            this._diffuseMethod = value;
            if (value) {
                this._diffuseChunk = value.getAbstraction(this._shader);
                this._addDependency(value);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MethodPass.prototype, "specularMethod", {
        /**
         * The method that provides the specular lighting contribution. Defaults to SpecularBasicMethod.
         */
        get: function () {
            return this._specularMethod;
        },
        set: function (value) {
            if (this._specularMethod == value)
                return;
            if (this._specularMethod) {
                this._removeDependency(this._specularMethod);
                this._specularChunk = null;
            }
            this._specularMethod = value;
            if (value) {
                this._specularChunk = value.getAbstraction(this._shader);
                this._addDependency(value);
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * @inheritDoc
     */
    MethodPass.prototype.dispose = function () {
        if (this._lightPicker)
            this._lightPicker.removeEventListener(AssetEvent.INVALIDATE, this._onLightsChangeDelegate);
        while (this._methods.length)
            this._removeDependency(this._methods[0]);
        _super.prototype.dispose.call(this);
        this._chunks = null;
        this._methods = null;
    };
    /**
     * Called when any method's shader code is invalidated.
     */
    MethodPass.prototype.onMethodInvalidated = function (event) {
        this.invalidate();
    };
    // RENDER LOOP
    /**
     * @inheritDoc
     */
    MethodPass.prototype._activate = function () {
        _super.prototype._activate.call(this);
        var chunk;
        var len = this._chunks.length;
        for (var i = 0; i < len; ++i) {
            chunk = this._chunks[i];
            if (chunk.chunkVO.useChunk)
                chunk._activate();
        }
    };
    /**
     *
     *
     * @param renderable
     * @param stage
     * @param camera
     */
    MethodPass.prototype._setRenderState = function (renderState) {
        _super.prototype._setRenderState.call(this, renderState);
        var chunk;
        var len = this._chunks.length;
        for (var i = 0; i < len; ++i) {
            chunk = this._chunks[i];
            if (chunk.chunkVO.useChunk)
                chunk._setRenderState(renderState);
        }
    };
    /**
     * @inheritDoc
     */
    MethodPass.prototype._deactivate = function () {
        _super.prototype._deactivate.call(this);
        var chunk;
        var len = this._chunks.length;
        for (var i = 0; i < len; ++i) {
            chunk = this._chunks[i];
            if (chunk.chunkVO.useChunk)
                chunk._deactivate();
        }
    };
    MethodPass.prototype._includeDependencies = function (shader) {
        _super.prototype._includeDependencies.call(this, shader);
        //TODO: fragment animtion should be compatible with lighting pass
        shader.usesFragmentAnimation = Boolean(this._mode == MethodPassMode.SUPER_SHADER);
        // if (shader.useAlphaPremultiplied && shader.usesBlending)
        // 	shader.usesCommonData = true;
        var i;
        var len = this._chunks.length;
        for (i = 0; i < len; ++i)
            this.setupAndCountDependencies(shader, this._chunks[i]);
        var usesTangentSpace = true;
        var chunk;
        for (i = 0; i < len; ++i) {
            chunk = this._chunks[i];
            if ((chunk.chunkVO.useChunk = chunk._isUsed()) && !chunk._usesTangentSpace())
                usesTangentSpace = false;
        }
        shader.outputsNormals = this._normalChunk && this._normalChunk.chunkVO.useChunk;
        shader.outputsTangentNormals = shader.outputsNormals && this._normalChunk._outputsTangentNormals();
        shader.usesTangentSpace = shader.outputsTangentNormals && !shader.usesProbes && usesTangentSpace;
        if (!shader.usesTangentSpace) {
            if (shader.viewDirDependencies > 0) {
                shader.globalPosDependencies++;
            }
            else if (this.numPointLights > 0 && shader.usesLights) {
                shader.globalPosDependencies++;
                if (this._mode & MethodPassMode.EFFECTS)
                    shader.usesGlobalPosFragment = true;
            }
        }
    };
    /**
     * Counts the dependencies for a given method.
     * @param method The method to count the dependencies for.
     * @param chunk The method's data for this material.
     */
    MethodPass.prototype.setupAndCountDependencies = function (shader, chunk) {
        var chunkVO = chunk.chunkVO;
        chunk._reset(chunkVO);
        chunk._initVO(chunkVO);
        if (chunkVO.needsProjection)
            shader.projectionDependencies++;
        if (chunkVO.needsGlobalVertexPos || chunkVO.needsGlobalFragmentPos) {
            shader.globalPosDependencies++;
            if (chunkVO.needsGlobalFragmentPos)
                shader.usesGlobalPosFragment = true;
        }
        if (chunkVO.needsNormals)
            shader.normalDependencies++;
        if (chunkVO.needsTangents)
            shader.tangentDependencies++;
        if (chunkVO.needsView)
            shader.viewDirDependencies++;
    };
    MethodPass.prototype._getPreLightingVertexCode = function (registerCache, sharedRegisters) {
        var code = '';
        return code;
    };
    MethodPass.prototype._getPreLightingFragmentCode = function (registerCache, sharedRegisters) {
        var code = '';
        if (this._diffuseChunk && this._diffuseChunk.chunkVO.useChunk)
            code += this._diffuseChunk._getFragmentPreLightingCode(registerCache, sharedRegisters);
        if (this._specularChunk && this._specularChunk.chunkVO.useChunk)
            code += this._specularChunk._getFragmentPreLightingCode(registerCache, sharedRegisters);
        return code;
    };
    MethodPass.prototype._getPerLightDiffuseFragmentCode = function (lightDirReg, diffuseColorReg, registerCache, sharedRegisters) {
        return this._diffuseChunk._getFragmentCodePerLight(lightDirReg, diffuseColorReg, registerCache, sharedRegisters);
    };
    MethodPass.prototype._getPerLightSpecularFragmentCode = function (lightDirReg, specularColorReg, registerCache, sharedRegisters) {
        return this._specularChunk._getFragmentCodePerLight(lightDirReg, specularColorReg, registerCache, sharedRegisters);
    };
    MethodPass.prototype._getPerProbeDiffuseFragmentCode = function (texReg, weightReg, registerCache, sharedRegisters) {
        return this._diffuseChunk._getFragmentCodePerProbe(texReg, weightReg, registerCache, sharedRegisters);
    };
    MethodPass.prototype._getPerProbeSpecularFragmentCode = function (texReg, weightReg, registerCache, sharedRegisters) {
        return this._specularChunk._getFragmentCodePerProbe(texReg, weightReg, registerCache, sharedRegisters);
    };
    MethodPass.prototype._getNormalVertexCode = function (registerCache, sharedRegisters) {
        return this._normalChunk._getVertexCode(registerCache, sharedRegisters);
    };
    MethodPass.prototype._getNormalFragmentCode = function (registerCache, sharedRegisters) {
        var code = this._normalChunk._getFragmentCode(sharedRegisters.normalFragment, registerCache, sharedRegisters);
        if (this._normalChunk.chunkVO.needsView)
            registerCache.removeFragmentTempUsage(sharedRegisters.viewDirFragment);
        if (this._normalChunk.chunkVO.needsGlobalFragmentPos || this._normalChunk.chunkVO.needsGlobalVertexPos)
            registerCache.removeVertexTempUsage(sharedRegisters.globalPositionVertex);
        return code;
    };
    /**
     * @inheritDoc
     */
    MethodPass.prototype._getVertexCode = function (registerCache, sharedRegisters) {
        var code = '';
        if (this._ambientChunk && this._ambientChunk.chunkVO.useChunk)
            code += this._ambientChunk._getVertexCode(registerCache, sharedRegisters);
        if (this._shadowChunk)
            code += this._shadowChunk._getVertexCode(registerCache, sharedRegisters);
        if (this._diffuseChunk && this._diffuseChunk.chunkVO.useChunk)
            code += this._diffuseChunk._getVertexCode(registerCache, sharedRegisters);
        if (this._specularChunk && this._specularChunk.chunkVO.useChunk)
            code += this._specularChunk._getVertexCode(registerCache, sharedRegisters);
        var chunk;
        var len = this._chunks.length;
        for (var i = len - this._numEffectDependencies; i < len; i++) {
            chunk = this._chunks[i];
            if (chunk.chunkVO.useChunk) {
                code += chunk._getVertexCode(registerCache, sharedRegisters);
                if (chunk.chunkVO.needsGlobalVertexPos || chunk.chunkVO.needsGlobalFragmentPos)
                    registerCache.removeVertexTempUsage(sharedRegisters.globalPositionVertex);
            }
        }
        if (this._colorTransformChunk && this._colorTransformChunk.chunkVO.useChunk)
            code += this._colorTransformChunk._getVertexCode(registerCache, sharedRegisters);
        return code;
    };
    /**
     * @inheritDoc
     */
    MethodPass.prototype._getFragmentCode = function (registerCache, sharedRegisters) {
        var code = '';
        if (this._ambientChunk && this._ambientChunk.chunkVO.useChunk) {
            code += this._ambientChunk._getFragmentCode(sharedRegisters.shadedTarget, registerCache, sharedRegisters);
            if (this._ambientChunk.chunkVO.needsNormals)
                registerCache.removeFragmentTempUsage(sharedRegisters.normalFragment);
            if (this._ambientChunk.chunkVO.needsView)
                registerCache.removeFragmentTempUsage(sharedRegisters.viewDirFragment);
        }
        return code;
    };
    MethodPass.prototype._getPostAnimationFragmentCode = function (registerCache, sharedRegisters) {
        var code = '';
        ///////////////begin lighting shading
        if (this._shadowChunk)
            code += this._shadowChunk._getFragmentCode(sharedRegisters.shadowTarget, registerCache, sharedRegisters);
        if (this._diffuseChunk && this._diffuseChunk.chunkVO.useChunk) {
            code += this._diffuseChunk._getFragmentCode(sharedRegisters.shadedTarget, registerCache, sharedRegisters);
            // resolve other dependencies as well?
            if (this._diffuseChunk.chunkVO.needsNormals)
                registerCache.removeFragmentTempUsage(sharedRegisters.normalFragment);
            if (this._diffuseChunk.chunkVO.needsView)
                registerCache.removeFragmentTempUsage(sharedRegisters.viewDirFragment);
        }
        if (this._specularChunk && this._specularChunk.chunkVO.useChunk) {
            code += this._specularChunk._getFragmentCode(sharedRegisters.shadedTarget, registerCache, sharedRegisters);
            if (this._specularChunk.chunkVO.needsNormals)
                registerCache.removeFragmentTempUsage(sharedRegisters.normalFragment);
            if (this._specularChunk.chunkVO.needsView)
                registerCache.removeFragmentTempUsage(sharedRegisters.viewDirFragment);
        }
        if (this._shadowChunk && !this._shader.normalDependencies)
            registerCache.removeFragmentTempUsage(sharedRegisters.shadowTarget);
        ///////////////end lighting shading
        var alphaReg;
        //check if alpha is preserved while performing effects shading and store value
        if (this.preserveAlpha && this._numEffectDependencies > 0) {
            alphaReg = registerCache.getFreeFragmentSingleTemp();
            registerCache.addFragmentTempUsages(alphaReg, 1);
            code += 'mov ' + alphaReg + ', ' + sharedRegisters.shadedTarget + '.w\n';
        }
        //perform effects shading
        var chunk;
        var len = this._chunks.length;
        for (var i = len - this._numEffectDependencies; i < len; i++) {
            chunk = this._chunks[i];
            if (chunk.chunkVO.useChunk) {
                code += chunk._getFragmentCode(sharedRegisters.shadedTarget, registerCache, sharedRegisters);
                if (chunk.chunkVO.needsNormals)
                    registerCache.removeFragmentTempUsage(sharedRegisters.normalFragment);
                if (chunk.chunkVO.needsView)
                    registerCache.removeFragmentTempUsage(sharedRegisters.viewDirFragment);
            }
        }
        //check if alpha is preserved while performing effects shading and restore value
        if (this.preserveAlpha && this._numEffectDependencies > 0) {
            code += 'mov ' + sharedRegisters.shadedTarget + '.w, ' + alphaReg + '\n';
            registerCache.removeFragmentTempUsage(alphaReg);
        }
        //peform final colortransform chunk if it exists
        if (this._colorTransformChunk && this._colorTransformChunk.chunkVO.useChunk)
            code += this._colorTransformChunk._getFragmentCode(sharedRegisters.shadedTarget, registerCache, sharedRegisters);
        return code;
    };
    /**
     * Indicates whether the shader uses any shadows.
     */
    MethodPass.prototype._iUsesShadows = function (shader) {
        return Boolean(this._shadowChunk && (this._lightPicker.castingDirectionalLights.length > 0 || this._lightPicker.castingPointLights.length > 0));
    };
    /**
     * Indicates whether the shader uses any specular component.
     */
    MethodPass.prototype._iUsesSpecular = function (shader) {
        return Boolean(this._specularChunk);
    };
    /**
     * Indicates whether the shader uses any specular component.
     */
    MethodPass.prototype._iUsesDiffuse = function (shader) {
        return Boolean(this._diffuseChunk);
    };
    MethodPass.prototype.onLightsChange = function (event) {
        this._updateLights();
    };
    MethodPass.prototype._updateLights = function () {
        var numDirectionalLightsOld = this.numDirectionalLights;
        var numPointLightsOld = this.numPointLights;
        var numLightProbesOld = this.numLightProbes;
        if (this._lightPicker && (this._mode & MethodPassMode.LIGHTING)) {
            this.numDirectionalLights = this.calculateNumDirectionalLights(this._lightPicker.numDirectionalLights);
            this.numPointLights = this.calculateNumPointLights(this._lightPicker.numPointLights);
            this.numLightProbes = this.calculateNumProbes(this._lightPicker.numLightProbes);
            if (this._includeCasters) {
                this.numDirectionalLights += this._lightPicker.numCastingDirectionalLights;
                this.numPointLights += this._lightPicker.numCastingPointLights;
            }
        }
        else {
            this.numDirectionalLights = 0;
            this.numPointLights = 0;
            this.numLightProbes = 0;
        }
        if (numDirectionalLightsOld != this.numDirectionalLights || numPointLightsOld != this.numPointLights || numLightProbesOld != this.numLightProbes) {
            this._updateShader();
            this.invalidate();
        }
    };
    /**
     * Calculates the amount of directional lights this material will support.
     * @param numDirectionalLights The maximum amount of directional lights to support.
     * @return The amount of directional lights this material will support, bounded by the amount necessary.
     */
    MethodPass.prototype.calculateNumDirectionalLights = function (numDirectionalLights) {
        return Math.min(numDirectionalLights - this.directionalLightsOffset, this._maxLights);
    };
    /**
     * Calculates the amount of point lights this material will support.
     * @param numDirectionalLights The maximum amount of point lights to support.
     * @return The amount of point lights this material will support, bounded by the amount necessary.
     */
    MethodPass.prototype.calculateNumPointLights = function (numPointLights) {
        var numFree = this._maxLights - this.numDirectionalLights;
        return Math.min(numPointLights - this.pointLightsOffset, numFree);
    };
    /**
     * Calculates the amount of light probes this material will support.
     * @param numDirectionalLights The maximum amount of light probes to support.
     * @return The amount of light probes this material will support, bounded by the amount necessary.
     */
    MethodPass.prototype.calculateNumProbes = function (numLightProbes) {
        var numChannels = 0;
        if ((this._renderMaterial.specularLightSources & LightSources.PROBES) != 0)
            ++numChannels;
        if ((this._renderMaterial.diffuseLightSources & LightSources.PROBES) != 0)
            ++numChannels;
        // 4 channels available
        return Math.min(numLightProbes - this.lightProbesOffset, (4 / numChannels) | 0);
    };
    return MethodPass;
}(PassBase));
export { MethodPass };
